import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Plus, Search } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useInviteProjectGroupsMembers } from "@/libs/react-query/hooks/useProjectQuery";
import { Button } from "@/components/ui/button";
import { GetGroupsWithUsers } from "@/services/Setting";
import { User } from "@/types/user";
import { GroupWithUsers, SettingUser } from "@/types/setting";
import { cn } from "@/libs/utils";
import CommonImg from "@/components/Common/Img";
import AddMemberIcon from "@/assets/images/add-member.svg";
import DeleteWhiteIcon from "@/assets/images/delete-white.svg";

// 전체 그룹을 위한 상수
const ALL_GROUP_ID = "all";

// 선택된 유저 타입 (그룹 정보 포함)
type SelectedUser = SettingUser & {
  groupName: string;
};

export default function InviteUserDialog({ users }: { users: User[] }) {
  const { group_id = "" } = useParams();

  const [open, setOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [groups, setGroups] = useState<GroupWithUsers[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(ALL_GROUP_ID);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);

  // 드롭다운에서 임시로 체크한 유저 ID들
  const [pendingUserIds, setPendingUserIds] = useState<Set<string>>(new Set());

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: inviteMember } = useInviteProjectGroupsMembers(group_id);

  // 모든 유저를 가져오기 (전체 그룹용)
  const getAllUsers = useCallback((): SettingUser[] => {
    const allUsers: SettingUser[] = [];
    const userIdSet = new Set<string>();

    groups.forEach((group) => {
      group.users.forEach((user) => {
        if (!userIdSet.has(user.user_id)) {
          userIdSet.add(user.user_id);
          allUsers.push(user);
        }
      });
    });

    return allUsers;
  }, [groups]);

  const fetchGroupsWithUsers = useCallback(async () => {
    try {
      const result = await GetGroupsWithUsers();
      if (result.data.success) {
        const groupsData = result.data.pods[0].content;
        setGroups(groupsData);
      }
    } catch (error) {
      console.error("그룹 및 사용자 정보 조회 중 오류 발생:", error);
    }
  }, []);

  const handleOpenChange = useCallback((value: boolean) => {
    setOpen(value);
    if (!value) {
      setSearchTerm("");
      setSelectedUsers([]);
      setSelectedGroupId(ALL_GROUP_ID);
      setShowDropdown(false);
      setPendingUserIds(new Set());
    }
  }, []);

  const handleInviteUsers = () => {
    if (selectedUsers.length === 0) return;

    inviteMember(
      {
        users: selectedUsers.map((user) => ({ user_id: user.user_id })),
      },
      {
        onSuccess: () => handleOpenChange(false),
      }
    );
  };

  const handleGroupClick = (groupId: string) => {
    setSelectedGroupId(groupId);
  };

  // 유저가 속한 그룹 이름 찾기
  const getUserGroupName = (user: SettingUser): string => {
    if (user.groups && user.groups.length > 0) {
      return user.groups[0].group_name;
    }
    // groups에서 해당 유저가 속한 그룹 찾기
    for (const group of groups) {
      if (group.users.some((u) => u.user_id === user.user_id)) {
        return group.group_name;
      }
    }
    return "";
  };

  // 팀 전체 추가 (임시 선택)
  const handleAddWholeTeam = (groupId: string) => {
    const newPendingIds = new Set(pendingUserIds);

    if (groupId === ALL_GROUP_ID) {
      getAllUsers().forEach((user) => {
        if (
          !users.some((u) => u.user_id === user.user_id) &&
          !selectedUsers.some((u) => u.user_id === user.user_id)
        ) {
          newPendingIds.add(user.user_id);
        }
      });
    } else {
      const group = groups.find((g) => g.group_id === groupId);
      group?.users.forEach((user) => {
        if (
          !users.some((u) => u.user_id === user.user_id) &&
          !selectedUsers.some((u) => u.user_id === user.user_id)
        ) {
          newPendingIds.add(user.user_id);
        }
      });
    }

    setPendingUserIds(newPendingIds);
  };

  // 드롭다운에서 유저 체크 토글 (임시 선택)
  const handleUserToggle = (userId: string) => {
    const newPendingIds = new Set(pendingUserIds);
    if (newPendingIds.has(userId)) {
      newPendingIds.delete(userId);
    } else {
      newPendingIds.add(userId);
    }
    setPendingUserIds(newPendingIds);
  };

  // 드롭다운의 "참여자 추가" 버튼 클릭 - 임시 선택된 유저들을 실제로 추가
  const handleConfirmPendingUsers = () => {
    const usersToAdd: SelectedUser[] = [];
    const allUsers = getAllUsers();

    pendingUserIds.forEach((userId) => {
      const user = allUsers.find((u) => u.user_id === userId);
      if (user && !selectedUsers.some((u) => u.user_id === userId)) {
        usersToAdd.push({ ...user, groupName: getUserGroupName(user) });
      }
    });

    setSelectedUsers((prev) => [...prev, ...usersToAdd]);
    setPendingUserIds(new Set());
    setShowDropdown(false);
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.user_id !== userId));
  };

  // 검색어로 그룹 필터링
  const filteredGroups = groups.filter((group) => {
    const searchLower = searchTerm.toLowerCase();
    if (group.group_name.toLowerCase().includes(searchLower)) {
      return true;
    }
    return group.users.some((user) => {
      const fullName = `${user.last_name}${user.first_name}`.toLowerCase();
      return fullName.includes(searchLower);
    });
  });

  // 선택된 그룹의 사용자 목록
  const getAvailableUsers = (): SettingUser[] => {
    if (selectedGroupId === ALL_GROUP_ID) {
      return getAllUsers().filter(
        (user) => !users.some((u) => u.user_id === user.user_id)
      );
    }

    const selectedGroup = groups.find((g) => g.group_id === selectedGroupId);
    return (
      selectedGroup?.users.filter(
        (user) => !users.some((u) => u.user_id === user.user_id)
      ) || []
    );
  };

  const availableUsers = getAvailableUsers();

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setPendingUserIds(new Set());
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      fetchGroupsWithUsers();
    }
  }, [open, fetchGroupsWithUsers]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <div className="flex items-center gap-2">
        <Avatar className="size-[24px]">
          <AvatarFallback></AvatarFallback>
        </Avatar>

        <DialogTrigger asChild>
          <div className="flex items-center gap-[2px] cursor-pointer">
            <span className="text-xs text-gray-B font-semibold">
              참여자 추가하기
            </span>
            <Plus size={16} className="text-gray-B" />
          </div>
        </DialogTrigger>
      </div>

      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-w-[640px] max-h-[1000px] p-8 flex flex-col"
      >
        <DialogHeader className="mb-4 shrink-0">
          <DialogTitle className="text-2xl font-bold text-gray-A text-center">
            참여자 추가하기
          </DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <div className="flex flex-col gap-6 min-h-0 flex-1">
          {/* 참여자 추가하기 검색 */}
          <div className="flex flex-col gap-2 relative shrink-0">
            <label className="text-sm font-semibold text-gray-A">
              참여자 추가하기
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                placeholder="참여자 이름으로 검색해보세요"
                className={cn(
                  "w-full h-11 pl-4 pr-10 border rounded-lg text-sm outline-none",
                  showDropdown
                    ? "border-dark-red"
                    : "border-gray-C focus:border-dark-red"
                )}
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-A" />
            </div>

            {/* 드롭다운 */}
            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute top-full left-0 right-0 mt-1 bg-white rounded-b shadow-[0px_1px_3px_0px_rgba(0,0,0,0.15)] z-50 flex flex-col"
              >
                <div className="flex max-h-[300px]">
                  {/* 왼쪽: 그룹 목록 */}
                  <div className="w-[200px] border-r border-gray-E overflow-y-auto">
                    {/* 전체 그룹 */}
                    <div
                      className={cn(
                        "relative px-4 h-[49px] cursor-pointer border-b border-gray-E flex items-center",
                        selectedGroupId === ALL_GROUP_ID && "bg-dark-red/20"
                      )}
                      onClick={() => handleGroupClick(ALL_GROUP_ID)}
                      onMouseEnter={() => setHoveredGroupId(ALL_GROUP_ID)}
                      onMouseLeave={() => setHoveredGroupId(null)}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="text-sm font-semibold text-black">
                          전체
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddWholeTeam(ALL_GROUP_ID);
                          }}
                          className={cn(
                            "h-6 px-2 rounded bg-dark-red text-white text-[8px] font-semibold hover:bg-dark-red/90 whitespace-nowrap",
                            hoveredGroupId !== ALL_GROUP_ID && "invisible"
                          )}
                        >
                          팀 전체 추가
                        </button>
                      </div>
                    </div>

                    {/* 그룹 목록 */}
                    {filteredGroups.map((group) => (
                      <div
                        key={group.group_id}
                        className={cn(
                          "relative px-4 h-[49px] cursor-pointer border-b border-gray-E flex items-center",
                          selectedGroupId === group.group_id && "bg-gray-E"
                        )}
                        onClick={() => handleGroupClick(group.group_id)}
                        onMouseEnter={() => setHoveredGroupId(group.group_id)}
                        onMouseLeave={() => setHoveredGroupId(null)}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="text-sm font-semibold text-dark-red">
                            {group.group_name}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddWholeTeam(group.group_id);
                            }}
                            className={cn(
                              "h-6 px-2 rounded bg-dark-red text-white text-[8px] font-semibold hover:bg-dark-red/90 whitespace-nowrap",
                              hoveredGroupId !== group.group_id && "invisible"
                            )}
                          >
                            팀 전체 추가
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 오른쪽: 사용자 목록 */}
                  <div className="flex-1 overflow-y-auto">
                    {availableUsers.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-B text-sm py-8">
                        추가 가능한 사용자가 없습니다
                      </div>
                    ) : (
                      <div className="pt-[10px]">
                        {availableUsers.map((user) => {
                          // 이미 모달에 추가된 유저인지 확인
                          const isAlreadyAdded = selectedUsers.some(
                            (u) => u.user_id === user.user_id
                          );
                          // 임시 선택 상태인지 확인
                          const isPending = pendingUserIds.has(user.user_id);
                          const groupName = getUserGroupName(user);

                          return (
                            <div
                              key={user.user_id}
                              className={cn(
                                "flex items-center justify-between px-4 py-3 border-b border-gray-E cursor-pointer hover:bg-gray-50",
                                (isPending || isAlreadyAdded) && "bg-gray-E",
                                isAlreadyAdded &&
                                  "opacity-50 cursor-not-allowed"
                              )}
                              onClick={() => {
                                if (!isAlreadyAdded) {
                                  handleUserToggle(user.user_id);
                                }
                              }}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <Avatar className="size-6">
                                  <AvatarImage src={user.image_url} />
                                  <AvatarFallback className="text-[10px]">
                                    {user.last_name}
                                    {user.first_name}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm font-semibold text-gray-A">
                                  {user.last_name}
                                  {user.first_name}
                                </span>
                                <span className="text-sm font-semibold text-dark-red">
                                  {groupName}
                                </span>
                                <span className="text-[10px] font-semibold text-gray-B">
                                  {user.email}
                                </span>
                              </div>
                              <div
                                className={cn(
                                  "size-[14px] rounded-sm flex items-center justify-center",
                                  isPending || isAlreadyAdded
                                    ? "bg-[#00A26B]"
                                    : "border border-gray-C bg-white"
                                )}
                              >
                                {(isPending || isAlreadyAdded) && (
                                  <Check
                                    className="size-3 text-white"
                                    strokeWidth={3}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* 드롭다운 내 참여자 추가 버튼 */}
                <div className="flex justify-center py-4 border-t border-gray-E">
                  <button
                    type="button"
                    className="flex items-center gap-2 bg-dark-red hover:bg-dark-red/90 text-white rounded-full px-5 py-2 shadow-[2px_3px_5px_0px_rgba(0,0,0,0.15)]"
                    onClick={handleConfirmPendingUsers}
                  >
                    <span className="text-sm font-semibold">참여자 추가</span>
                    <CommonImg
                      src={AddMemberIcon}
                      alt="add-member"
                      className="size-4"
                    />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 선택된 참여자 목록 */}
          {selectedUsers.length > 0 && (
            <div className="flex flex-col gap-6 overflow-y-auto min-h-0">
              {selectedUsers.map((user) => (
                <div
                  key={user.user_id}
                  className="flex items-center justify-between shrink-0"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <Avatar className="size-6">
                      <AvatarImage src={user.image_url} />
                      <AvatarFallback className="text-[10px]">
                        {user.last_name}
                        {user.first_name}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-semibold text-gray-A">
                      {user.last_name}
                      {user.first_name}
                    </span>
                    <span className="text-sm font-semibold text-dark-red">
                      {user.groupName}
                    </span>
                    <span className="text-[10px] font-semibold text-gray-B">
                      {user.email}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveUser(user.user_id)}
                    className="size-6 flex items-center justify-center bg-pink rounded"
                  >
                    <CommonImg
                      src={DeleteWhiteIcon}
                      alt="delete"
                      className="size-3"
                    />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* 하단 버튼 */}
          {!showDropdown && (
            <div className="flex justify-center gap-4 pt-4 shrink-0">
              <Button
                type="button"
                variant="cancel"
                onClick={() => handleOpenChange(false)}
              >
                취소하기
              </Button>
              <Button
                type="button"
                variant="confirm"
                disabled={selectedUsers.length === 0}
                onClick={handleInviteUsers}
              >
                추가하기
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
